// ============================================
// 2026-10-17（标绘层 S3）：贴地/吸附（纯函数，可单测）
//  - 网格吸附 / 模型包围盒候选点吸附（绘制与拖拽共用）
//  - 线段按采样点逐个贴地（模型表面射线由调用侧提供 resolveY）
// ============================================
import type { P3 } from './primitives';

export interface BoxLike {
  min: [number, number, number];
  max: [number, number, number];
}

/** 网格吸附（x/z 取 step 倍数；y 不变；step<=0 原样返回） */
export function snapToGrid(p: P3, step: number): P3 {
  if (!(step > 0)) return [p[0], p[1], p[2]];
  return [Math.round(p[0] / step) * step, p[1], Math.round(p[2] / step) * step];
}

/** 收集包围盒吸附候选点（8 角 + 中心） */
export function collectBoxSnapPoints(boxes: BoxLike[]): P3[] {
  const out: P3[] = [];
  for (const b of boxes) {
    const [x0, y0, z0] = b.min;
    const [x1, y1, z1] = b.max;
    out.push([x0, y0, z0], [x1, y0, z0], [x0, y0, z1], [x1, y0, z1]);
    out.push([x0, y1, z0], [x1, y1, z0], [x0, y1, z1], [x1, y1, z1]);
    out.push([(x0 + x1) / 2, (y0 + y1) / 2, (z0 + z1) / 2]);
  }
  return out;
}

/** 就近吸附（水平距离 ≤ maxDist 才吸附；保持原点 y；未命中原样返回） */
export function snapToCandidates(p: P3, candidates: P3[], maxDist = 2.5): P3 {
  let best: P3 | null = null;
  let bestD = Math.max(0, maxDist);
  for (const c of candidates) {
    const d = Math.hypot(c[0] - p[0], c[2] - p[2]);
    if (d < bestD) { bestD = d; best = c; }
  }
  if (!best) return [p[0], p[1], p[2]];
  return [best[0], p[1], best[2]];
}

/** 线段按采样点逐个贴地：每段按 maxSegLen 细分，resolveY 返回 null 时保留插值 y */
export function groundPointsToSurface(
  points: P3[],
  resolveY: (x: number, z: number) => number | null,
  maxSegLen = 2,
): P3[] {
  if (points.length === 0) return [];
  const step = Math.max(0.1, maxSegLen);
  const out: P3[] = [];
  const push = (p: P3) => {
    const y = resolveY(p[0], p[2]);
    out.push([p[0], y === null ? p[1] : y, p[2]]);
  };
  push(points[0]);
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    const d = Math.hypot(b[0] - a[0], b[2] - a[2]);
    const steps = Math.max(1, Math.ceil(d / step));
    for (let s = 1; s <= steps; s++) {
      const t = s / steps;
      push([
        a[0] + (b[0] - a[0]) * t,
        a[1] + (b[1] - a[1]) * t,
        a[2] + (b[2] - a[2]) * t,
      ]);
    }
  }
  return out;
}
